<template>
  <div
    v-if="isLoaded && !isServerError"
    class="explorer-search text-big"
    style="max-width: 800px; margin: 0 auto"
  >
    <div>
      <h2>Gyre Recommends</h2>
      <div v-if="canProduceStatement()" class="jyranian-speech-bubble">
        <div class="search-callout-2">
          <div v-if="canRecommendAtLeastTwoResources()">
            <p>
              <b>{{ statementCapiltalized }}</b
              >{{ continuation }}
            </p>

            <p>
              <span data-test="jyre-recommendation-text"
                >Gyre recommends
                <a
                  :href="
                    cms.linkResolver({
                      uid: results[0].situation.uid,
                      type: results[0].situation.type,
                      id: results[0].situation.id,
                    })
                  "
                  data-test="recommended-result-1"
                  >{{ results[0].situation.title }}</a
                >
                and
                <a
                  :href="
                    cms.linkResolver({
                      uid: results[1].situation.uid,
                      type: results[1].situation.type,
                      id: results[1].situation.id,
                    })
                  "
                  data-test="recommended-result-2"
                  >{{ results[1].situation.title }}</a
                >
              </span>

              {{ summaryContinuation }}
            </p>
          </div>
          <p v-else data-test="no-recommendations-found">
            Sorry, we couldn't find any relevant resources in Gyre to recommend.
          </p>
        </div>
        <div class="jyranian-container" v-if="canRecommendAtLeastTwoResources()">
          <img
            src="../../assets/smart-search/jyre-recommends-jyranian.svg"
            alt="A Wise Jyranian"
            data-test="jyranian-recommends"
            class="results-jyranian"
          />
        </div>
        <div v-else class="error-jyranian-container">
          <img
            src="../../assets/jyranians/jyranian-telescope.png"
            alt="a jyranian with a telescope"
            data-test="jyranian-recommends-no-results"
            class="error-jyranian"
          />
        </div>
      </div>

      <!--      <div>-->
      <!--        <span v-if="suggestions.length">See also: </span>-->
      <!--        <span class="list-unstyled" style="margin-left: 5px">-->
      <!--          <span v-for="suggestion in suggestions" v-bind:key="suggestion">-->
      <!--            <a href="javascript:void(0)" @click="takeSuggestion(suggestion)"> {{ suggestion }}</a>-->
      <!--          </span>-->
      <!--        </span>-->
      <!--      </div>-->

      <hr class="hr-smart-search" />

      <!--      <h3 v-if="results[0]">Key Resources</h3>-->
      <div
        v-bind:key="result.situation.id"
        v-for="(result, index) in filterVisible(results)"
        data-test="recommendation-results"
      >
        <!--        <h3 v-if="index != 0 && isKey(results, index - 1) && !isKey(results, index)">-->
        <!--          Other Resources-->
        <!--        </h3>-->
        <div>
          <p>
            <span
              v-if="false"
              class="search-result-type label label-default"
              :class="['search-result-type-' + result.situation.type]"
              >{{ " " || getResourceTypeName(result) }}</span
            >
            <span
              v-if="isRecommended(index)"
              class="temp_tooltip"
              style="font-size: 22px; line-height: 0.6; margin-right: 7px; user-select: none"
              ><span class="temp_tooltiptext">Recommended</span
              >{{ result.ratings.logProbWords > 5 ? "☆☆" : "☆" }}</span
            >
            <span
              class="temp_tooltip"
              v-else
              style="
                font-size: 10px;
                white-space: pre-wrap;
                text-align: center;
                line-height: 0.4;
                user-select: none;
                position: relative;
                top: 1px;
                display: inline-block;
                margin-right: 7px;
              "
              ><span class="temp_tooltiptext">{{
                getRelevancies(result.ratings.logProbWords)
              }}</span
              >{{ getDots(result.ratings.logProbWords) }}</span
            >
            <a
              :href="
                cms.linkResolver({
                  uid: result.situation.uid,
                  type: result.situation.type,
                  id: result.situation.id,
                })
              "
              :class="{ 'result-highlight': false }"
              ><b>{{ result.situation.title }}</b></a
            >
            <span v-if="isRecommended(index)">
              {{ result.situation.displaySummary.replace("This article", "") }}
            </span>
            <span v-else>
              {{ result.situation.displaySummary.replace("This article", "") }}
            </span>
          </p>
          <div class="search-callout search-callout-2" v-if="result.keyPoints[0]">
            {{ result.keyPoints[0] }}
          </div>

          <div v-if="false && (result.keyPoints || [])[0]">
            <p>
              <span>Reflections:</span>
            </p>
            <ul>
              <li v-bind:key="point" v-for="point in result.keyPoints || []">{{ point }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
  <SmartSearchError v-else-if="isLoaded && isServerError" />
  <div v-else style="height: 200px">
    <SmartSearchLoadingAnimation data-test="jyre-recommends-loading-spinner" />
    <p style="margin: 0 auto; text-align: center">{{ percentLoaded }}%</p>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable */
import { ref, computed, onMounted } from "vue";
import { temp_tooltip } from "uiv";
import { v4 as uuid } from "uuid";
import { debug as d } from "debug";
import { AxiosError } from "axios";
import cms from "../../services/cms/cms";
import { complete } from "./complete";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";
import getSearchSuggestions from "./search-suggestions";
import { massRate } from "./search-rate";
import { Analytics } from "../../services/analytics/analytics";
import SearchResult from "./smart-search-result";
import { processSituations } from "./process-situations";
import LoadingPage from "../../views/loading-page.vue";
import SmartSearchError from "./smart-search-error.vue";
import SmartSearchLoadingAnimation from "./smart-search-loading-animation.vue";

const debug = d("cs.smartSearch");

(window as any).processSituations = processSituations;

const jyreRecommendsPrompt = (query, titleA, titleB, descA, descB) => `${descA}

${descB}

The user wants to know about '${query}'.

Write a two sentences explaining that website Gyre recommends ${titleA} and ${titleB}.

Gyre recommends ${titleA} and ${titleB}`;

const guid = uuid as () => string;

const props = defineProps<{
  query: string;
}>();

const smartSearchSessionId = ref(guid());
const smartSearchRunId = ref("");
const statement = ref((window as any)._jyreStatement || "");
const continuation = ref((window as any)._jyreContinuation || "");
const DISPLAYED_RESULTS_COUNT = 2;
const isServerError = ref(false);

const hasFocus = ref(false);
const lastQuery = ref<string | null>(null);

const sampleQuestion = ref("");

const active = computed(() => {
  return (
    props.query !== lastQuery.value &&
    (props.query.trim() || (!hasFocus.value && !props.query.trim()))
  );
});

const suggestions = ref<string[]>([]);

const DOTS = [
  "-",
  "◇\n◇◇\n◇",
  "◈\n◇◇\n◇",
  "◈\n◇◇\n◇",
  "◈\n◈◇\n◇",
  "◈\n◈◈\n◇",
  "◈\n◈◈\n◈",
  "◆\n◈◈\n◈",
  "◆\n◆◈\n◈",
  "◆\n◆◆\n◈",
  "◆\n◆◆\n◆",
];

const RELEVANCIES = [
  "Not relevant",
  "May be slightly relevant",
  "May be somewhatrRelevant",
  "May be relevant",
  "Somewhat relevant",
  "Partially relevant",
  "Generally relevant",
  "Relevant",
  "Quite relevant",
  "Very relevant",
  "Highly relevant",
];

const results = ref<SearchResult[]>((window as any)._jyreSearchResults || []);

function isRecommended(index: number) {
  return index <= 1 && results.value[index].ratings.logProbWords > 4.9;
}

function getDots(outOf5: number) {
  const i = Math.round(Math.min(outOf5, 5) * 2);
  return DOTS[i];
}

function getRelevancies(outOf5: number) {
  const i = Math.round(Math.min(outOf5, 5) * 2);
  return RELEVANCIES[i];
}

const open = computed(() => {
  return false;
});

function isKey(searchResults: SearchResult[], index: number) {
  return searchResults[index].ratings.logProbWords >= 3.75 && index < 4;
}

function isVisible(searchResults: SearchResult[], index: number) {
  return searchResults[index].ratings.logProbWords > 1.25 && index < 10;
}

function filterVisible(searchResults: SearchResult[]) {
  return searchResults
    .filter((r, i) => isVisible(searchResults, i))
    .slice(0, DISPLAYED_RESULTS_COUNT);
}

const canRecommendAtLeastTwoResources = () => {
  return filterVisible(results.value).length > 1 && results.value[0] && results.value[1];
};

const canProduceStatement = () => {
  return results.value && results.value[0] && statement.value;
};

const statementCapiltalized = computed(() => {
  return statement.value.charAt(0).toUpperCase() + statement.value.slice(1);
});

async function runMixpanelAction(
  query: string,
  currentSmartSearchSessionId: string,
  currentSmartSearchRunId: string,
) {
  return new Promise((resolve) => {
    setTimeout(() => {
      Analytics.action("Ran Smart Search", "SmartSearchActions", {
        query,
        smartSearchSessionId: currentSmartSearchSessionId,
        smartSearchRunId: currentSmartSearchRunId,
      });
      resolve(true);
    }, 0);
  });
}

const isLoaded = ref(false);

const summaryContinuation = ref("");

const percentLoaded = computed(() => {
  let x = 0;
  if (results.value.length) {
    x += 29;
  }
  if (statement.value) {
    x += 5;
  }
  if (continuation.value) {
    x += 7;
  }
  if (summaryContinuation.value) {
    x += 17;
  }
  x += (results.value?.filter((x) => x.keyPoints?.length).length ?? 0) * 23;
  return x;
});

async function setAdvice(result: SearchResult) {
  const { title } = result.situation;
  const { text } = result.situation;
  const currentStatement = statement.value.trim().replace(/[?.]$/, "");
  const part1 = `This article's Key Ideas, and how they help with ${statementCapiltalized.value}:`;
  const part1b = (i) => `Key Idea: ____\nHow it helps with ${currentStatement}: ____`;

  const template = `Template\n${part1}\n${part1b(0)}\n\n${part1b(1)}\n\n${part1b(
    3,
  )}\n\nOverall, ____\n\n\n\n`;
  const prompt = `Subject: ${
    statementCapiltalized.value
  }, and how the article's Key Ideas can help.\n${[1, 2, 3]
    .map((x) => statementCapiltalized.value)
    .join(
      "\n",
    )}\n\n\n\nArticle: ${title}\n\n${text}\n\n\n\n${template}\n\n${`HOW DO I GO ABOUT ${statementCapiltalized.value.toUpperCase()}?`}\n\nEdited for clarity.\n\n${part1}\n\n|>|Key Idea: This article`;
  return complete(prompt, {
    token_bias: {
      "How": 5,
      "To": 0,
      ":": -5,
      "<|endoftext|>": -7,
      " This": -5,
      " this": -3,
      " These": -5,
      " these": -3,
      " By": -5,
      " Additionally": -5,
      " also": -5,
      " Also": -5,
      " It": -5,
    },
    stop: ["\nEdited", "\nOverall", "\nKey"],
  }).then((resp) => {
    result.story = resp;
    result.keyPoints = resp
      .split(/Key Idea:/gi)
      .map((x) => x.split(/\s*How it[^:^\n]*:\s*/)[1]?.trim() ?? "")
      .filter((x) => x);
    return result.keyPoints[0];
  });
}

async function run() {
  const updatedSmartSearchRunId = (smartSearchRunId.value = guid());
  const updatedSmartSearchSessionId = (smartSearchSessionId.value = guid());
  lastQuery.value = props.query;
  const currentQuery = props.query.trim();
  let currentStatement = (statement.value = "");
  let currentContinuation = (continuation.value = "");
  let currSummaryContinuation = (summaryContinuation.value = "");
  let currentResults = (results.value = [] as SearchResult[]);
  const currentSuggestions = (suggestions.value = [] as string[]);

  await runMixpanelAction(currentQuery, updatedSmartSearchRunId, updatedSmartSearchSessionId);

  const gptCutoff = 50;
  const setResultsPromise = massRate(currentQuery, gptCutoff).then(async (r) => {
    results.value = currentResults = (window as any)._jyreSearchResults = r;
    return r;
  });

  const getStatementPromise = complete(
    `I'm currently using the Gyre platform, a personal, team, and leadership development tool. I've just talked to the Gyre agent about '${currentQuery.trim()}'.\n\nQuestion: What do I want information and help with? What do I want to do?\nAnswer: You want information & help with`,
    {
      frequency_penalty: 0,
      presence_penalty: 0,
      temperature: 0,
      stop: ["\n", ".", ":", "?"],
      token_bias: {
        " it": -100,
        " this": -100,
        " that": -100,
        "/": -100,
        " /": -100,
        " how": -10,
        " Personal": -3,
        " Team": -3,
        " Leadership": -3,
        " personal": -3,
        " team": -3,
        " leadership": -3,
        " the": -2,
      },
    },
  );

  const setContinuationPromise = getStatementPromise.then(async (x) => {
    const y = x.trim();
    statement.value = currentStatement = (window as any)._jyreStatement = y;
    await complete(
      `Gyre is a personal, team, and leadership development website. Continue the sentence '${statementCapiltalized.value}' in an empathetic & insightful way.\n\n${statement.value}`,
      {
        stop: ["\n", ".", ":", ",,"],
        max_tokens: 150,
        token_bias: {
          ".": -2,
          ":": -100,
          ",": 2,
          " essential": -5,
          " any": -5,
          " success": -5,
          " an": -2,
          " important": -5,
          " successful": -2,
        },
      },
    ).then((z) => (continuation.value = currentContinuation = (window as any)._jyreContinuation = `${z}.`));
  });

  await setResultsPromise;

  const setSummaryContinuationPromise = setResultsPromise.then(async (r) =>
    getSummaryContinuation(currentQuery, r, await getStatementPromise).then(
      (s) => (summaryContinuation.value = currSummaryContinuation = s),
    ),
  );

  // We have to wait until the results search is complete before getting search suggestions to properly use our quota.
  // let setSearchSuggestionsPromise = setResultsPromise.then(async (r) =>
  //   getSearchSuggestions(query).then((x) => (this.suggestions = x)),
  // );

  const advice = ["", "", "", ""] as string[];

  const setAdvicePromise = setResultsPromise.then(async (r) => {
    // justification(x.slice(0, 4), this.query);
    await Promise.all([
      setAdvice(r[0]),
      setAdvice(r[1]),
      // this.setAdvice(r[2]),
      // this.setAdvice(r[3]),
    ]);
  });

  await setContinuationPromise;
  await setResultsPromise;
  await setAdvicePromise;
  // await setSearchSuggestionsPromise;
  await setSummaryContinuationPromise;

  const analysedResults = currentResults.filter((r) => r.ratings.embeddingIndex < gptCutoff);

  const data = {
    query: currentQuery,
    statement: currentStatement,
    continuation,
    summary: `Gyre recommends "${currentResults[0].situation.title}" and "${
      currentResults[1].situation.title
    }" ${currSummaryContinuation.trim()}`,
    suggestions: suggestions.value.map((s) => s.slice(0, 250)),
    currentResults: filterVisible(currentResults).map(
      (r, i) =>
        `${r.situation.title}, ${getRelevancies(r.ratings.logProbWords)}${
          isKey(currentResults, i) ? ", Key" : ""
        }`,
    ),
    advice: currentResults.slice(0, 4).map((x) => (x.keyPoints[0] || "").slice(0, 250)),

    info: analysedResults.map((r, i) =>
      `${r.situation.type}/${r.situation.uid}@${i}~${
        r.ratings.embeddingIndex
      };${r.ratings.logProbWords.toString().replace(/(\d\d\d\d\d\d)\d*/g, "$1")}<${JSON.stringify(
        r.ratings.tokenProbs,
      ).replace(/(\d\d\d\d)\d*/g, "$1")}`.slice(0, 250),
    ),
  };

  debug(data);
  isLoaded.value = true;

  Analytics.action("Completed Smart Search", "SmartSearchActions", data);
}

async function getSummaryContinuation(q: string, searchResults: SearchResult[], statement: string) {
  const a = results.value[0].situation;
  const b = results.value[1].situation;

  const titleA = a.title;
  const titleB = b.title;

  const descA = a.displaySummary.replace("This article", 'The article "' + titleA + '"');
  const descB = b.displaySummary.replace("This article", 'The article "' + titleB + '"');

  const prompt = jyreRecommendsPrompt(props.query, titleA, titleB, descA, descB);

  return complete(prompt);
}

function getResourceTypeName(result: SearchResult): string {
  switch (result.situation.type as CmsDocumentTypes) {
    case CmsDocumentTypes.CmsResource:
      return "Resource";
    case CmsDocumentTypes.CmsLeaderType:
      return "Leader type";
    case CmsDocumentTypes.CmsLinkResource:
      return "Resource";
    case CmsDocumentTypes.CmsStrengthItem:
      return "Strength";
    case CmsDocumentTypes.CmsSession:
    case CmsDocumentTypes.CmsSessionWithSlides:
      return "Session";
    case CmsDocumentTypes.CmsWorkshopPage:
      return "Step";
    case CmsDocumentTypes.CmsResourcePlaylist:
      return "Playlist";
    case CmsDocumentTypes.CmsResourceTheme:
      return "Theme";
    case CmsDocumentTypes.CmsSessionWheel:
      return "Wheel";
    case CmsDocumentTypes.CmsTeamPathway:
      return "Team Pathway";
    case CmsDocumentTypes.CmsPersonalPathway:
      return "Personal Pathway";
    default:
      return "Other";
  }
}

onMounted(async () => {
  try {
    await run();
  } catch (e) {
    isLoaded.value = true;
    isServerError.value = true;
    const error = e as AxiosError;
    const errorMessage = `${error.response?.status}: ${error.response?.statusText}`;
    Analytics.action("Smart Search Error", "SmartSearchActions", {
      query: props.query,
      smartSearchSessionId,
      smartSearchRunId: smartSearchRunId.value,
      error: errorMessage,
    });
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.explorer-search-textarea {
  border: none;
  overflow: hidden;
  outline: none;
  width: 100%;
  background: none;
  line-height: 27px;
  margin: 0;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 10px;

  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;

  resize: none; /*remove the resize handle on the bottom right*/
}

//https://www.w3schools.com/css/css_temp_tooltip.asp
.explorer-search {
  /* temp_tooltip container */

  .temp_tooltip {
    position: relative;
    display: inline-block;
    // border-bottom: 1px dotted black; /* If you want dots under the hoverable text */
  }

  /* temp_tooltip text */

  .temp_tooltip .temp_tooltiptext {
    visibility: hidden;
    width: 150px;
    background-color: black;
    color: #fff;
    text-align: center;
    padding: 5px 0;
    border-radius: 6px;
    font-size: 12px !important;

    /* Position the temp_tooltip text - see examples below! */
    position: absolute;
    top: -20px;
    left: -0px;
    z-index: 1;
  }

  /* Show the temp_tooltip text when you mouse over the temp_tooltip container */

  .temp_tooltip:hover .temp_tooltiptext {
    visibility: visible;
  }
}

.explorer-search-page {
  border-radius: 15px;
  box-shadow: 0px 0px 4px;
  padding-top: 4px;
}

.result-highlight {
  color: blue;
}

.explorer-search {
  .search-callout {
    padding: 20px;
    margin: 20px 0;
    border: 1px solid #eee;
    background-color: white;
    border-left-width: 5px;
    border-radius: 3px;
  }

  .search-callout-2 {
    padding: 20px;
    margin: 20px 0;
    border: 1px solid @charismatic-color;
    background-color: lighten(@charismatic-color, 50%);
    border-left-width: 5px;
    border-radius: 3px;
  }

  .jyranian-speech-bubble {
    display: flex;
    align-items: flex-start;

    @media (max-width: @screen-xs-max) {
      flex-direction: column;
      align-items: flex-end;
    }
  }

  .jyranian-container {
    margin-left: -140px;
    margin-right: -90px;
    pointer-events: none;
    @media (max-width: @screen-xs-max) {
      margin: -90px 0 0 0;
      .results-jyranian {
        width: 300px;
        margin-right: -5rem;
      }
    }
  }

  .error-jyranian-container {
    max-width: 35%;
    pointer-events: none;

    .error-jyranian {
      transform: scaleX(-1);
      width: 150%;
      margin-left: -120px;
    }

    @media (max-width: @screen-xs-max) {
      margin: -90px 0 0 0;
      max-width: 100%;
      overflow: hidden;
      .error-jyranian {
        width: 400px;
        margin-right: -3rem;
      }
    }
  }

  .search-result-type {
    float: right;
    margin-top: 10px;
  }

  .c-cms-resource {
    background-color: coral;
  }

  .search-result-type-cms-workshop-page {
    background-color: blueviolet;
  }

  .search-result-type-cms-session-with-slides {
    background-color: orange;
  }

  .search-result-type-cms-resource-playlist {
    background-color: green;
  }

  .search-result-type-cms-pathway {
    background-color: #17a2b8;
  }

  .search-result-type-cms-personal-pathway {
    background-color: #17a2b8;
  }

  .explorer-search-textarea-watermark {
    width: 100%;
    height: 110px;
    position: absolute;
    top: 0;
    left: 0;
    background: none;
    z-index: 0;
    color: #aaa;
  }
}

.hr-smart-search {
  margin: 5rem 0;
  border-color: @form-text-color;
}
</style>
